import {
	ParsedGuideWithVersionsConfig,
	ParsedLinkConfig,
	ParsedPageConfig,
	ParsedPageWithContent,
	ParsedSectionConfig,
} from './schemas'

/**
 * Walks through the sidebar items and calls the callback for each item.
 * It also walks through the pages of each item.
 */
export function walkSidebarItems<
	T extends ParsedSectionConfig | ParsedPageWithContent | ParsedLinkConfig | ParsedPageConfig,
>(items: T[], callback: (item: T) => void) {
	for (const item of items) {
		callback(item)
		if ('pages' in item && Array.isArray(item.pages)) {
			walkSidebarItems(item.pages as T[], callback)
		}
	}
}

export function walkSidebarItemsWithAdditionalInfo<
	T extends ParsedSectionConfig | ParsedPageWithContent | ParsedLinkConfig | ParsedPageConfig,
	U,
>(items: T[], additionalInfo: U, callback: (item: T, additionalInfoForChildren: U) => U) {
	for (const item of items) {
		const additionalInfoForChildren = callback(item, additionalInfo)
		if ('pages' in item && Array.isArray(item.pages)) {
			walkSidebarItemsWithAdditionalInfo(item.pages as T[], additionalInfoForChildren, callback)
		}
	}
}

function applyCallbackToPage<U extends ParsedPageWithContent>(
	page: ParsedPageWithContent,
	callback: (item: ParsedPageWithContent) => U,
) {
	const updatedPage = callback(page)

	if (updatedPage.pages && updatedPage.pages.length > 0) {
		return {
			...updatedPage,
			pages: updatedPage.pages.map((page) => applyCallbackToPage(page, callback)),
		}
	}

	return updatedPage
}

export function applyGuideAndVersionPages<
	T extends ParsedGuideWithVersionsConfig,
	U extends ParsedPageWithContent,
>(guides: T[], callback: (item: ParsedPageWithContent) => U) {
	return guides.map(({ versions, ...rest }) => {
		return {
			...rest,
			versions: versions.map(({ hiddenPages, sidebar, ...versionRest }) => {
				return {
					...versionRest,
					hiddenPages: hiddenPages.map((page) => callback(page)),
					sidebar: sidebar.map((item) => {
						if (item.type === 'page') {
							return applyCallbackToPage(item, callback)
						} else if (item.type === 'section') {
							if (item.pages && item.pages.length > 0) {
								return {
									...item,
									pages: item.pages.map((page) => applyCallbackToPage(page, callback)),
								}
							}
							return item
						} else {
							return item
						}
					}),
				}
			}),
		}
	})
}
